import { useMemo } from 'react';

import getLang from 'I18n/utils/getLang';

import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { RequestStateValue } from 'marketplace-ui-common/requestStates/RequestStateTypes';
import { AppContextType } from 'marketplace-ui-common/types';

import {
  type LegacyReviewResponse,
  ReviewType,
} from '../../__generated__/chirp/com/hubspot/reviews/rpc/ReviewsRpc';
import { useSearchReviewsLegacyQuery } from '../../__generated__/chirp/com/hubspot/reviews/rpc/ReviewsRpc.hooks';
import * as LIST_QUERIES from '../constants/ReviewsListQueries';
import Review, { ReviewResponseJSON } from '../records/Review';
import ReviewFilters, { ReviewFiltersRecord } from '../records/ReviewFilters';
import { ReviewTypeUnion } from '../types';

interface UseSearchReviewsLegacyProps {
  appContext: AppContextType;
  // For APP/PROFILE types, pass the framework listing ID (not the offering/profile ID).
  entityId: number;
  reviewFilters: ReviewFilters;
  reviewType: ReviewTypeUnion;
  skip?: boolean;
}

export function useSearchReviewsLegacy({
  appContext,
  entityId,
  reviewFilters,
  reviewType,
  skip,
}: UseSearchReviewsLegacyProps): {
  reviews: Review[];
  reviewsFetchStatus: RequestStateValue;
  reviewsTotal: number;
} {
  const language = getLang();
  const rf = reviewFilters as unknown as ReviewFiltersRecord;
  const filterState = LIST_QUERIES.STATE_TO_QUERY[rf.filter] ?? {};
  const sortState = LIST_QUERIES.STATE_TO_QUERY[rf.sort] ?? {};
  const offset = (rf.page - 1) * rf.limit;

  const { data, loading, error } = useSearchReviewsLegacyQuery({
    variables: {
      entityId,
      reviewTypesEnums: [reviewType as ReviewType],
      language,
      limit: rf.limit,
      offset,
      sortFields: sortState.sortFields ?? [LIST_QUERIES.SORT.NEWEST],
      overallRating: filterState.overallRating ?? undefined,
      search: rf.search || undefined,
      reviewerPortalId: rf.reviewerPortalId ?? undefined,
      reviewerUserId: rf.reviewerUserId ?? undefined,
      ...(rf.employees
        ? { employeeSizes: { clause: 'OR', values: [rf.employees] } }
        : {}),
      ...(rf.industry
        ? { industries: { clause: 'OR', values: [rf.industry] } }
        : {}),
      ...(rf.service
        ? {
            tagFilters: [
              {
                group: 'CATALOG_SERVICES',
                values: [rf.service],
                clause: 'OR',
              },
            ],
          }
        : {}),
    },
    // external auth (chirp-frontend-external) requires a logged-in user; skip for public pages.
    skip: appContext === 'public' || !entityId || skip,
  });

  return useMemo(
    () => ({
      reviews: Review.fromArray(
        (data?.reviews ?? []).map((r: LegacyReviewResponse) => ({
          ...(r as unknown as ReviewResponseJSON),
          // legacyReviewId is the v1 review ID; use it over the default id
          // field once the BE adds it to LegacyReviewResponse.
          id: (r as any).legacyReviewId ?? r.id,
        })),
        reviewType
      ),
      reviewsFetchStatus: getDataFetchingClientRequestState(
        error,
        loading
      ) as RequestStateValue,
      reviewsTotal: data?.total ?? 0,
    }),
    [data, error, loading, reviewType]
  );
}
